import { Check, ScanBarcode } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { IMG } from "@/lib/images";

const ITEMS = [
  { name: "Blood and Pathology Tests", desc: "Dependable routine delivery of whole blood, plasma, and pathology tissue to and from medical laboratories.", red: true },
  { name: "Covid Test Samples", desc: "Premium transport for COVID-19 test kits and swab results, made easy.", red: true },
  { name: "Drug and Medicine", desc: "Specialized delivery of drugs and medicine to and from medical laboratories on a regular basis.", red: true },
  { name: "Medical Records", desc: "Expert, secure delivery of medical records and confidential documents for the healthcare industry.", red: true },
  { name: "Urine Samples", desc: "Trusted by laboratories and medical institutions for routine urine test delivery.", red: true },
  { name: "Prosthetics and Implants", desc: "Custom surgical orthotics, implant devices, and dental prosthetics handled with precision.", red: true },
  { name: "Medical Equipment and Devices", desc: "Diagnostic monitors, infusion pumps, and small healthcare equipment.", red: true },
];

export const WhatWeDeliver = () => (
  <section id="deliver" data-testid="deliver-section" className="bg-[#F8FAFC] py-24 lg:py-32 scroll-mt-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-h-[640px] w-full shadow-2xl shadow-blue-900/15 border border-white">
          <img src="/assets/stat-specimen.png" alt="Stat specimen cooler with biohazard bags and blood vials, navy graded"
               data-testid="deliver-specimen-image" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D4F]/65 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl px-5 py-4 flex items-center gap-3">
            <span className="h-9 w-9 rounded-xl bg-[#0047FF] flex items-center justify-center shrink-0">
              <ScanBarcode className="h-4 w-4 text-white" />
            </span>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">Barcode-Scanned Custody</p>
              <p className="text-[11px] font-mono text-slate-500 tracking-wider uppercase">Pickup → delivery · fully logged</p>
            </div>
          </div>
        </div>
        <div className="absolute -top-5 -right-3 sm:-right-6 glass rounded-2xl px-5 py-3.5 shadow-xl shadow-blue-900/10">
          <p className="font-display text-xl font-extrabold text-[#0047FF] leading-none">Same-Day</p>
          <p className="mt-1 text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-slate-500">Across the DMV</p>
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <Reveal>
          <Overline>What We Can Deliver — Chapter 02</Overline>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            If it matters to a patient, it rides with us.
          </h2>
        </Reveal>
        <div className="mt-10 space-y-5">
          {ITEMS.map((item, i) => (
            <Reveal key={item.name} delay={i * 0.05}>
              <div data-testid={`deliver-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                   className="flex items-start gap-4 group">
                <span className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-110 ${
                  item.red ? "bg-red-500 shadow-red-500/30" : "bg-[#0047FF] shadow-blue-500/30"
                }`}>
                  <Check className="h-4 w-4 text-white" strokeWidth={3.2} />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900 tracking-tight">{item.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
